import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { HiOutlineDownload, HiOutlinePhotograph, HiOutlineCheckCircle } from "react-icons/hi";
import {
  getAvailablePlaceholders,
  generatePreviewFilename,
} from "../utils/imageDownloadUtils";
import { DOWNLOAD_PHASES } from "../hooks/useImageDownload";

// Rotating messages to show during download
const WAITING_MESSAGES = [
  { title: "Fetching images from source...", subtitle: "Downloading each image from its original URL" },
  { title: "Processing images...", subtitle: "Images are being compressed into a ZIP file" },
  { title: "Still working...", subtitle: "Large downloads may take a moment" },
  { title: "Almost there...", subtitle: "Packaging your images for download" },
  { title: "Hang tight...", subtitle: "The server is handling the heavy lifting" },
  { title: "Working on it...", subtitle: "Your download will start automatically" },
];

/**
 * Format elapsed time in a human-readable way
 */
const formatElapsedTime = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

/**
 * Modal for configuring and downloading images from a table
 */
const ImageDownloadModal = ({
  isOpen,
  onClose,
  imageColumns,
  columns,
  currentPageData,
  tableHeader,
  totalRows,
  isDarkMode,
  downloadState,
  onDownload,
  getPreviewImages,
  getEstimatedCount,
}) => {
  // Selected columns state
  const [selectedColumns, setSelectedColumns] = useState([]);

  // Naming template state
  const [namingTemplate, setNamingTemplate] = useState("{column_name}_{index}");

  // Elapsed time tracking
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef(null);

  // Message rotation index
  const [messageIndex, setMessageIndex] = useState(0);

  // Initialize selected columns when modal opens
  useEffect(() => {
    if (isOpen && imageColumns.length > 0) {
      setSelectedColumns(imageColumns);
    }
  }, [isOpen, imageColumns]);

  // Timer for elapsed time
  useEffect(() => {
    if (downloadState.isDownloading) {
      setElapsedSeconds(0);
      setMessageIndex(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [downloadState.isDownloading]);

  // Rotate messages every 4 seconds after initial 3 seconds
  useEffect(() => {
    if (downloadState.isDownloading && elapsedSeconds >= 3) {
      const cycleIndex = Math.floor((elapsedSeconds - 3) / 4) % WAITING_MESSAGES.length;
      setMessageIndex(cycleIndex);
    }
  }, [elapsedSeconds, downloadState.isDownloading]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && !downloadState.isDownloading) onClose();
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEscape);
      return () => window.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onClose, downloadState.isDownloading]);

  // Get preview images
  const previewImages = useMemo(() => {
    if (!getPreviewImages || selectedColumns.length === 0) return [];
    return getPreviewImages(selectedColumns, 8);
  }, [getPreviewImages, selectedColumns]);

  // Get estimated count
  const estimatedCount = useMemo(() => {
    if (!getEstimatedCount || selectedColumns.length === 0) return 0;
    return getEstimatedCount(selectedColumns);
  }, [getEstimatedCount, selectedColumns]);

  // Get available placeholders
  const placeholders = useMemo(() => {
    return getAvailablePlaceholders(columns || []);
  }, [columns]);

  // Preview filename
  const previewFilename = useMemo(() => {
    if (selectedColumns.length === 0 || !currentPageData?.length) return "";
    const sampleRow = currentPageData[0];
    const columnName = selectedColumns[0]?.name || "image";
    return generatePreviewFilename(namingTemplate, sampleRow, columnName, columns || []);
  }, [namingTemplate, selectedColumns, currentPageData, columns]);

  // Toggle column selection
  const toggleColumn = useCallback((column) => {
    setSelectedColumns((prev) => {
      const isSelected = prev.some((c) => c.id === column.id);
      if (isSelected) {
        return prev.filter((c) => c.id !== column.id);
      }
      return [...prev, column];
    });
  }, []);

  // Handle download
  const handleDownloadClick = useCallback(() => {
    onDownload(selectedColumns, namingTemplate);
  }, [onDownload, selectedColumns, namingTemplate]);

  // Get progress info based on phase and elapsed time
  const getProgressInfo = useCallback(() => {
    switch (downloadState.phase) {
      case DOWNLOAD_PHASES.PREPARING:
        return {
          title: "Preparing download...",
          subtitle: "Gathering image URLs from your data",
        };
      case DOWNLOAD_PHASES.DOWNLOADING: {
        // Use rotating messages after a few seconds
        if (elapsedSeconds < 3) {
          return {
            title: "Starting download...",
            subtitle: "Connecting to server",
          };
        }
        return WAITING_MESSAGES[messageIndex];
      }
      case DOWNLOAD_PHASES.COMPLETE:
        return {
          title: "Download started!",
          subtitle: "Check your browser's downloads",
          isComplete: true,
        };
      case DOWNLOAD_PHASES.ERROR:
        return {
          title: "Download failed",
          subtitle: downloadState.error || "An error occurred",
          isError: true,
        };
      default:
        return null;
    }
  }, [downloadState, elapsedSeconds, messageIndex]);

  if (!isOpen) return null;

  const progressInfo = getProgressInfo();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={() => !downloadState.isDownloading && onClose()}
      />

      {/* Modal */}
      <div
        className={`relative rounded-xl border shadow-2xl w-full max-w-md mx-4 overflow-hidden ${
          isDarkMode
            ? "bg-[#111113] border-white/[0.08]"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`px-4 py-3 border-b ${
            isDarkMode ? "border-white/[0.06]" : "border-gray-100"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div
                className={`p-1.5 rounded-md ${
                  isDarkMode ? "bg-blue-500/10" : "bg-blue-50"
                }`}
              >
                <HiOutlineDownload
                  className={`w-4 h-4 ${
                    isDarkMode ? "text-blue-400" : "text-blue-500"
                  }`}
                />
              </div>
              <div>
                <h2
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Download Images
                </h2>
                <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                  {estimatedCount > 0 ? `~${estimatedCount.toLocaleString()} images` : "Select columns"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={downloadState.isDownloading}
              className={`p-1 rounded-md transition-colors disabled:opacity-50 ${
                isDarkMode
                  ? "hover:bg-white/[0.06] text-gray-500"
                  : "hover:bg-gray-100 text-gray-400"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-3 max-h-[55vh] overflow-y-auto">
          {/* Progress Section - Show when downloading */}
          {downloadState.isDownloading && progressInfo && (
            <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? "bg-white/[0.03]" : "bg-gray-50"}`}>
              <div className="flex items-center gap-3">
                {progressInfo.isComplete ? (
                  <HiOutlineCheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <div className="relative">
                    <svg className="w-5 h-5 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {progressInfo.title}
                  </p>
                  <p className={`text-xs truncate ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {progressInfo.subtitle}
                  </p>
                </div>
                {/* Elapsed time badge */}
                {elapsedSeconds > 0 && (
                  <span className={`text-xs tabular-nums px-2 py-0.5 rounded-full ${
                    isDarkMode ? "bg-white/[0.06] text-gray-400" : "bg-gray-200 text-gray-500"
                  }`}>
                    {formatElapsedTime(elapsedSeconds)}
                  </span>
                )}
              </div>

              {/* Indeterminate progress bar */}
              <div className={`mt-3 h-1 rounded-full overflow-hidden ${isDarkMode ? "bg-white/[0.06]" : "bg-gray-200"}`}>
                <div className="h-full bg-blue-500 animate-progress-indeterminate" />
              </div>

              {/* Reassurance message for long waits */}
              {elapsedSeconds >= 10 && (
                <p className={`mt-2 text-[10px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                  {estimatedCount > 50
                    ? `Processing ~${estimatedCount} images. This window will update when complete.`
                    : "Please keep this window open. Download will start automatically."
                  }
                </p>
              )}
            </div>
          )}

          {/* Column Selection - Compact inline */}
          {!downloadState.isDownloading && (
            <>
              <div className="mb-3">
                <label className={`text-xs font-medium mb-1.5 block ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  Image Columns
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {imageColumns.map((column) => {
                    const isSelected = selectedColumns.some((c) => c.id === column.id);
                    return (
                      <button
                        key={column.id}
                        onClick={() => toggleColumn(column)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs transition-all ${
                          isSelected
                            ? isDarkMode
                              ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                              : "bg-blue-50 text-blue-600 border border-blue-200"
                            : isDarkMode
                            ? "bg-white/[0.04] text-gray-400 border border-white/[0.06] hover:bg-white/[0.08]"
                            : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                        }`}
                      >
                        <HiOutlinePhotograph className="w-3 h-3" />
                        <span className="truncate max-w-[120px]">{column.name}</span>
                        {isSelected && (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Preview Grid - Smaller images */}
              {previewImages.length > 0 && (
                <div className="mb-3">
                  <label className={`text-xs font-medium mb-1.5 block ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    Preview
                  </label>
                  <div className="flex gap-1.5 overflow-x-auto pb-1">
                    {previewImages.slice(0, 8).map((img, idx) => (
                      <div
                        key={idx}
                        className={`w-10 h-10 flex-shrink-0 rounded-md overflow-hidden ${
                          isDarkMode ? "bg-white/[0.04]" : "bg-gray-100"
                        }`}
                      >
                        <img
                          src={img.url}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      </div>
                    ))}
                    {estimatedCount > 8 && (
                      <div
                        className={`w-10 h-10 flex-shrink-0 rounded-md flex items-center justify-center text-xs ${
                          isDarkMode ? "bg-white/[0.04] text-gray-500" : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        +{(estimatedCount - 8).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Naming Convention - Compact */}
              <div className="mb-3">
                <label className={`text-xs font-medium mb-1.5 block ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  File Naming
                </label>
                <input
                  type="text"
                  value={namingTemplate}
                  onChange={(e) => setNamingTemplate(e.target.value)}
                  placeholder="{column_name}_{index}"
                  className={`w-full px-2.5 py-1.5 text-xs rounded-md border transition-colors ${
                    isDarkMode
                      ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-500 focus:border-blue-500/50"
                      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                  } focus:outline-none focus:ring-1 focus:ring-blue-500/20`}
                />
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {placeholders.slice(0, 4).map((p) => (
                    <button
                      key={p.key}
                      onClick={() => setNamingTemplate((prev) => prev + p.key)}
                      title={p.description}
                      className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                        isDarkMode
                          ? "bg-white/[0.04] text-gray-500 hover:bg-white/[0.08] hover:text-gray-300"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                      }`}
                    >
                      {p.key}
                    </button>
                  ))}
                </div>
                {previewFilename && (
                  <p className={`text-[10px] mt-1 font-mono ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}>
                    → {previewFilename}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Error */}
          {downloadState.error && !downloadState.isDownloading && (
            <div className={`flex items-start gap-2 p-2.5 rounded-md text-xs ${
              isDarkMode ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
            }`}>
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{downloadState.error}</span>
            </div>
          )}

          {/* Success */}
          {progressInfo?.isComplete && (
            <div className={`flex items-start gap-2 p-2.5 rounded-md text-xs ${
              isDarkMode ? "bg-green-500/10 text-green-400" : "bg-green-50 text-green-600"
            }`}>
              <HiOutlineCheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Download started! Check your browser's downloads.</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`px-4 py-3 border-t flex items-center justify-between ${
            isDarkMode ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50/50 border-gray-100"
          }`}
        >
          <div className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            {selectedColumns.length > 0 && !downloadState.isDownloading && (
              <span>{selectedColumns.length} column{selectedColumns.length !== 1 ? "s" : ""} selected</span>
            )}
            {downloadState.isDownloading && elapsedSeconds > 0 && (
              <span>Working... please wait</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={downloadState.isDownloading}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-white/[0.06]"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              {progressInfo?.isComplete ? "Close" : "Cancel"}
            </button>
            {!progressInfo?.isComplete && (
              <button
                onClick={handleDownloadClick}
                disabled={downloadState.isDownloading || selectedColumns.length === 0}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all disabled:opacity-50 flex items-center gap-1.5 ${
                  isDarkMode
                    ? "bg-blue-500 text-white hover:bg-blue-600"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
              >
                {downloadState.isDownloading ? (
                  <>
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <HiOutlineDownload className="w-3 h-3" />
                    <span>Download</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* CSS for indeterminate progress animation */}
      <style>{`
        @keyframes progress-indeterminate {
          0% { transform: translateX(-100%); width: 50%; }
          50% { transform: translateX(50%); width: 50%; }
          100% { transform: translateX(200%); width: 50%; }
        }
        .animate-progress-indeterminate {
          animation: progress-indeterminate 1.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ImageDownloadModal;
